import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { otpAdminAPI } from '../../services/api';

/**
 * Admin: OTP provider control. Switch phone-OTP between Firebase (default) and
 * MSG91 (the OTP Widget — much cheaper) globally and/or per app (role). Only the
 * NEW app builds that call /auth/send-otp honor this; older builds stay on
 * Firebase. 'msg91' only takes effect when the server has the MSG91 widget creds
 * (msg91Configured); otherwise the backend auto-falls-back to Firebase.
 */
const ROLES = [
  { key: 'delivery', label: '🛵 Delivery app', hint: 'Roll MSG91 out here first — smallest audience.' },
  { key: 'restaurant', label: '🏪 Restaurant app', hint: 'Partners; roll out after delivery is proven.' },
  { key: 'user', label: '👤 Customer app', hint: 'Largest audience — roll out last.' },
];

function OtpSettings() {
  const [cfg, setCfg] = useState(null);
  const [msg91Configured, setMsg91Configured] = useState(false);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await otpAdminAPI.getSettings();
      const d = r.data?.data || {};
      const ov = d.providerOverrides || {};
      setCfg({
        provider: d.provider === 'msg91' ? 'msg91' : 'firebase',
        overrides: {
          user: ov.user || '',
          restaurant: ov.restaurant || '',
          delivery: ov.delivery || '',
        },
      });
      setMsg91Configured(!!d.msg91Configured);
    } catch (e) {
      setMsg('Failed to load OTP settings');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await otpAdminAPI.updateSettings({ provider: cfg.provider, providerOverrides: cfg.overrides });
      setMsg('Saved ✓');
      load();
    } catch (e) {
      setMsg(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return (<div><Navbar /><div style={{ padding: 24 }}>Loading…</div></div>);

  // effective provider for a role = its override, else the global default.
  const effective = (role) => (cfg.overrides[role] && cfg.overrides[role] !== '' ? cfg.overrides[role] : cfg.provider);

  const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' };
  const label = { display: 'block', fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 };
  const select = { padding: '9px 11px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, background: '#fff', minWidth: 190 };
  const pill = (p) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: p === 'msg91' ? '#DCFCE7' : '#E0E7FF', color: p === 'msg91' ? '#166534' : '#3730A3',
  });
  const seg = (active) => ({
    padding: '9px 18px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', border: '1px solid',
    borderColor: active ? '#2563EB' : '#E2E8F0', background: active ? '#2563EB' : '#fff', color: active ? '#fff' : '#334155',
  });

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f8' }}>
      <Navbar />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', margin: '0 0 4px' }}>🔐 OTP Provider</h1>
        <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 18px' }}>
          Choose which service sends login OTPs — <b>Firebase</b> (default) or <b>MSG91</b> (the cheaper OTP Widget).
          Set a global default and/or override per app. Roll out one app at a time.
        </p>

        {/* MSG91 readiness */}
        <div style={{ ...card, borderColor: msg91Configured ? '#86EFAC' : '#FCA5A5', background: msg91Configured ? '#F0FDF4' : '#FEF2F2' }}>
          <div style={{ fontWeight: 700, color: msg91Configured ? '#166534' : '#B91C1C', fontSize: 14 }}>
            {msg91Configured ? '✅ MSG91 widget is configured on the server' : '⚠️ MSG91 is NOT configured'}
          </div>
          <div style={{ fontSize: 12.5, color: '#64748B', marginTop: 4 }}>
            {msg91Configured
              ? 'Setting an app to MSG91 will take effect immediately for its new build.'
              : 'Set MSG91_AUTH_KEY + MSG91_WIDGET_ID + MSG91_WIDGET_TOKEN in the server env. Until then, any ‘MSG91’ selection safely falls back to Firebase.'}
          </div>
        </div>

        {/* Global default */}
        <div style={card}>
          <label style={label}>Global default</label>
          <div style={{ display: 'flex', gap: 10 }}>
            {['firebase', 'msg91'].map((p) => (
              <button key={p} style={seg(cfg.provider === p)} onClick={() => setCfg({ ...cfg, provider: p })}>
                {p === 'msg91' ? 'MSG91' : 'Firebase'}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
            Applies to every app that doesn’t have its own override below.
          </div>
        </div>

        {/* Per-app overrides */}
        <div style={card}>
          <label style={label}>Per-app override</label>
          {ROLES.map((r) => {
            const eff = effective(r.key);
            const willFallback = eff === 'msg91' && !msg91Configured;
            return (
              <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderTop: '1px solid #f1f5f9' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#0F172A' }}>{r.label}</div>
                  <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{r.hint}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <select
                    style={select}
                    value={cfg.overrides[r.key]}
                    onChange={(e) => setCfg({ ...cfg, overrides: { ...cfg.overrides, [r.key]: e.target.value } })}
                  >
                    <option value="">Use global default ({cfg.provider === 'msg91' ? 'MSG91' : 'Firebase'})</option>
                    <option value="firebase">Firebase</option>
                    <option value="msg91">MSG91</option>
                  </select>
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    Effective: <span style={pill(eff)}>{eff === 'msg91' ? 'MSG91' : 'Firebase'}</span>
                    {willFallback && <span style={{ color: '#B91C1C', marginLeft: 8, fontSize: 11.5 }}>→ falls back to Firebase (not configured)</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            onClick={save}
            disabled={saving}
            style={{ background: '#16A34A', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 22px', fontSize: 15, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {msg && <span style={{ fontWeight: 600, color: msg.includes('✓') ? '#166534' : '#B91C1C' }}>{msg}</span>}
        </div>

        <div style={{ ...card, marginTop: 18, background: '#F8FAFC' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#334155', marginBottom: 6 }}>How this works</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: '#64748B', fontSize: 12.5, lineHeight: 1.7 }}>
            <li>Only the <b>latest app builds</b> honor this (they ask the server which provider to use). Older installed builds keep using Firebase.</li>
            <li>If MSG91 send ever fails, the app <b>automatically falls back to Firebase</b> — logins never break.</li>
            <li><b>Instant rollback:</b> set an app back to “Use global default” (Firebase), then Save.</li>
            <li>Firebase push notifications (FCM) are unaffected either way.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default OtpSettings;
